import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { TutorialProvider } from './contexts/TutorialContext';
import { UnsavedGuardProvider } from './contexts/UnsavedGuardContext';
import { TutorialOverlay } from './components/TutorialOverlay';
import { ConsentGate } from './components/ConsentGate';
import { Toaster } from './components/ui/sonner';
import { WhatsAppFloatingButton } from './components/WhatsAppFloatingButton';
import { capturePageview } from './utils/observability';

import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import OnboardingPage from './pages/OnboardingPage';
import DashboardPage from './pages/DashboardPage';
import PatientsPage from './pages/PatientsPage';
import PatientDetailPage from './pages/PatientDetailPage';
import ConsultationsPage from './pages/ConsultationsPage';
import NewConsultationPage from './pages/NewConsultationPage';
import ConsultationFlowPage from './pages/ConsultationFlowPage';
import ConsultationDetailPage from './pages/ConsultationDetailPage';
import TranscriptionPage from './pages/TranscriptionPage';
import SettingsPage from './pages/SettingsPage';
import BillingPage from './pages/BillingPage';
import BillingSuccessPage from './pages/BillingSuccessPage';
import CreditsSuccessPage from './pages/CreditsSuccessPage';
import AdminDashboard from './pages/AdminDashboard';
import AdminOverview from './pages/AdminOverview';
import AdminUsers from './pages/AdminUsers';
import AdminBilling from './pages/AdminBilling';
import AdminSystem from './pages/AdminSystem';
import AdminLoginPage from './pages/AdminLoginPage';
import AdminMembersPage from './pages/AdminMembersPage';
import AdminUserConsultations from './pages/AdminUserConsultations';
import AdminInviteAcceptPage from './pages/AdminInviteAcceptPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import UpgradePage from './pages/UpgradePage';

// Capture PostHog pageviews on every SPA route change.
const PageviewTracker = () => {
  const location = useLocation();
  React.useEffect(() => {
    capturePageview(location.pathname + location.search);
  }, [location.pathname, location.search]);
  return null;
};

const INSTITUTIONAL_URL = 'https://www.akaliai.com.br';

const HomeRedirect = () => {
  const { user, loading } = useAuth();

  React.useEffect(() => {
    if (!loading && !user) {
      window.location.replace(INSTITUTIONAL_URL);
    }
  }, [loading, user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  return null;
};

const PrivateRoute = ({ children, allowAdmin = false }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  // Admins must use the admin panel — redirect from regular user routes,
  // unless the route explicitly allows admins (e.g. /change-password, /onboarding).
  if (user.is_admin && !allowAdmin && !location.pathname.startsWith('/admin')) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  if (!user.onboarding_completed && location.pathname !== '/onboarding' && !user.is_admin) {
    return <Navigate to="/onboarding" />;
  }

  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/admin-akali-2026" />;
  }

  // Single source of truth: is_admin is computed by the backend from admin_service.ADMIN_EMAILS
  if (!user.is_admin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <TutorialProvider>
            <UnsavedGuardProvider>
            <PageviewTracker />
            <TutorialOverlay />
            <ConsentGate>
            <Routes>
              <Route path="/" element={<HomeRedirect />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/signup" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route
                path="/onboarding"
                element={
                  <PrivateRoute>
                    <OnboardingPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <DashboardPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/patients"
                element={
                  <PrivateRoute>
                    <PatientsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/patients/:id"
                element={
                  <PrivateRoute>
                    <PatientDetailPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/consultations"
                element={
                  <PrivateRoute>
                    <ConsultationsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/consultations/new"
                element={
                  <PrivateRoute>
                    <ConsultationFlowPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/consultations/new/:patientId"
                element={
                  <PrivateRoute>
                    <ConsultationFlowPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/consultation-flow/:patientId"
                element={
                  <PrivateRoute>
                    <ConsultationFlowPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/consultations/:id"
                element={
                  <PrivateRoute>
                    <ConsultationDetailPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/transcription"
                element={
                  <PrivateRoute>
                    <TranscriptionPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <PrivateRoute>
                    <SettingsPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/billing"
                element={
                  <PrivateRoute>
                    <BillingPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/upgrade"
                element={
                  <PrivateRoute>
                    <UpgradePage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/billing/success"
                element={
                  <PrivateRoute>
                    <BillingSuccessPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/billing/credits-success"
                element={
                  <PrivateRoute>
                    <CreditsSuccessPage />
                  </PrivateRoute>
                }
              />
              <Route path="/admin-akali-2026" element={<AdminLoginPage />} />
              <Route path="/admin-invite" element={<AdminInviteAcceptPage />} />

              {/* Redirect /admin -> /admin/dashboard for back-compat */}
              <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />

              <Route
                path="/admin/dashboard"
                element={
                  <AdminRoute>
                    <AdminOverview />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <AdminRoute>
                    <AdminUsers />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/billing"
                element={
                  <AdminRoute>
                    <AdminBilling />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/system"
                element={
                  <AdminRoute>
                    <AdminSystem />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/members"
                element={
                  <AdminRoute>
                    <AdminMembersPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/legacy"
                element={
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/users/:userId/consultations"
                element={
                  <AdminRoute>
                    <AdminUserConsultations />
                  </AdminRoute>
                }
              />
              <Route
                path="/change-password"
                element={
                  <PrivateRoute>
                    <ChangePasswordPage />
                  </PrivateRoute>
                }
              />
              <Route
                path="/admin/change-password"
                element={
                  <AdminRoute>
                    <ChangePasswordPage />
                  </AdminRoute>
                }
              />
            </Routes>
            <Toaster position="top-right" richColors />
            <WhatsAppFloatingButton />
            </ConsentGate>
            </UnsavedGuardProvider>
          </TutorialProvider>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
